import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { BasketView } from 'ish-core/models/basket/basket.model';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { whenTruthy } from 'ish-core/utils/operators';
import { ErrorMessageComponent } from 'ish-shared/components/common/error-message/error-message.component';
import { SuccessMessageComponent } from 'ish-shared/components/common/success-message/success-message.component';

/**
 * The Basket Promotion Component displays a promotion code input.
 * It provides the add promotion code functionality
 *
 * @example
 * <ish-basket-promotion-code></ish-basket-promotion-code>
 */
@Component({
  selector: 'ish-basket-promotion-code',
  templateUrl: './basket-promotion-code.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FaIconComponent,
    ErrorMessageComponent,
    SuccessMessageComponent,
    NgbCollapse,
    ReactiveFormsModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class BasketPromotionCodeComponent implements OnInit, OnDestroy {
  private checkoutFacade = inject(CheckoutFacade);
  private cd = inject(ChangeDetectorRef);

  readonly toast = input(true);

  basket$: Observable<BasketView>;
  promotionError$: Observable<HttpError>;
  codeInput: FormControl;
  isCollapsed = true;
  codeMaxLength = 128;

  basketPromoCodes: string[];
  lastEnteredPromoCode = '';

  private destroy$ = new Subject<void>();

  constructor() {
    const library = inject(FaIconLibrary);

    library.addIconPacks(fas);
  }

  ngOnInit() {
    this.basket$ = this.checkoutFacade.basket$;
    this.promotionError$ = this.checkoutFacade.promotionError$;

    this.codeInput = new FormControl('', [Validators.required, Validators.maxLength(this.codeMaxLength)]);

    // update emitted to display spinning animation
    this.basket$.pipe(whenTruthy(), takeUntil(this.destroy$)).subscribe(basket => {
      this.basketPromoCodes = basket.promotionCodes;
      if (this.displaySuccessMessage) {
        this.codeInput.reset();
        this.isCollapsed = true;
      }
      this.cd.detectChanges();
    });
  }

  /**
   * submit promotion code when add promotion code was clicked.
   */
  submitPromotionCode() {
    // prevent success message if the user enters the same promo code twice
    if (!this.basketPromoCodes?.includes(this.codeInput.value)) {
      this.lastEnteredPromoCode = this.codeInput.value;
    }
    this.checkoutFacade.addPromotionCodeToBasket(this.codeInput.value);
    // prevent further form submit
    return false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displaySuccessMessage(): boolean {
    return this.basketPromoCodes?.includes(this.lastEnteredPromoCode);
  }
}
